import {
  assignAttributeStateToLegends,
  assignAttributeStateToTLLegends,
  convertTimeToMinutes,
  generateLegend,
  getMergedYAxisList,
  getPropertyAxisAndIsAdded,
  getTimeIndex,
  intervalToMinutes,
  legendName
} from "../common"
import moment from "moment"
import { isArray, isString, toNumber } from "lodash"
import {
  chartDateFormat,
  constantValues,
  reduxStoreTimeStampFormat
} from "../../constant"
import {
  TIMELINE_PROPERTY_TYPE,
  transformTimeLinechartNew
} from "./timeLineChart"
import { generateExceptionBars } from "../Exceptions/index"
import { generateSuppressionBars } from "../Suppressions"
import { generateLegendProps_Type } from "../../ChartContainer/Types/helper"
import { generateExceptionLegends } from "../Exceptions/helper"
import { generateSuppressionLegends } from "../Suppressions/helper"
export const transformScatterChart = ({
  tz,
  startDate,
  endDate,
  intervalMinutes = "15m",
  inputData,
  chartInfo,
  equipmentList,
  buildingId,
  equipmentId,
  addedProperties,
  oldLegends,
  oldTimeLineLegends = null,
  assignedAttributes = [],
  legendAttributesCombination
}) => {
  let calculatedMin = intervalToMinutes(intervalMinutes)
  let minimumCalculatedMin = intervalToMinutes(intervalMinutes)
  let minInterval = calculatedMin
  const defaultCalculatedMin = intervalToMinutes(intervalMinutes)
  let lastIndexPosition = (60 / calculatedMin) * 24 - 1
  const equipmentType = chartInfo?.equipmentType
  const legends = {}
  let timeLineLegends = {}
  const addedAxisList = addedProperties?.axisList || []
  const data = inputData?.data || {}
  const newData = {}
  const newDataTimeline = {}
  const allTimestampsObject = {}
  const localTimeStamp = Boolean(tz)
    ? moment(
        moment.tz(moment(), tz).format("MM-DD-YYYY HH:mm"),
        "MM-DD-YYYY HH:mm"
      )
    : moment()
  const endTimestamp = moment(endDate, chartDateFormat).add(1, "days")
  for (const id in data) {
    const paramData = data[id]
    const equipmentName = equipmentList?.find((obj) => obj?.id === id)?.name
    const selectedEquipment = equipmentId === id
    const building = buildingId === id
    const legendEquipmentType = equipmentList?.find(
      (obj) => obj?.id === id
    )?.type

    const properties = paramData?.properties
    const sortedDates = Object.keys(paramData?.values || {})
      .filter((date) => date != "frq")
      .sort(
        (a: string, b: string) =>
          new Date(a).getTime() - new Date(b).getTime()
      )

    for (const propertyKey in properties) {
      calculatedMin = defaultCalculatedMin
      minInterval = defaultCalculatedMin
      lastIndexPosition = (60 / minInterval) * 24 - 1
      const property = properties[propertyKey]
      const propertyIndex = property?.index
      const propertyName = property?.name || ""

      const { axis, isUserAddedProperty, legendId } =
        getPropertyAxisAndIsAdded({
          chartInfo,
          propertyKey,
          equipmentType,
          equipmentList,
          id,
          addedAxisList
        })
      const name = legendName({
        building,
        selectedEquipment,
        propertyName,
        equipmentName
      })
      const xType = axis?.axisType?.toLowerCase() === "x"
      let legendObj
      if (axis?.axisName === "timeline") {
        legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          propertyKey,
          type: "timeline",
          isUserAddedProperty,
          oldLegends: oldTimeLineLegends,
          propertyType: TIMELINE_PROPERTY_TYPE
        })
        timeLineLegends[legendId] = legendObj
      } else {
        const obj: generateLegendProps_Type = {
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          isUserAddedProperty,
          legendEquipmentType,
          oldLegends
        }
        if (xType) {
          obj.additionalAttributes = {
            xAxis: true,
            disabled: true
          }
        }
        legendObj = generateLegend(obj)
        legends[legendObj?.id] = legendObj
      }

      let currentTimestamp = moment(startDate, chartDateFormat).locale("en")
      let prevValue = null
      while (
        currentTimestamp.isSameOrBefore(endTimestamp) &&
        currentTimestamp.isSameOrBefore(localTimeStamp)
      ) {
        const dateKey = currentTimestamp.format("YYYY-MM-DD")
        const extractTime = currentTimestamp.format("HH:mm")
        const timetoMin = convertTimeToMinutes(extractTime)

        const dateAvailable = paramData?.values?.[dateKey]
        if (dateAvailable) {
          const dateIndexPosition = sortedDates.indexOf(dateKey)
          const extractedInterval =
            paramData.values?.frq?.[dateIndexPosition]?.split("|")?.[
              propertyIndex
            ]
          calculatedMin = extractedInterval / 60 || defaultCalculatedMin
          minInterval =
            paramData?.values?.[dateKey]?.["intervalInSeconds"] / 60 ||
            defaultCalculatedMin
          lastIndexPosition = (60 / minInterval) * 24 - 1

          if (calculatedMin < minimumCalculatedMin) {
            minimumCalculatedMin = calculatedMin
          }
        }

        const timeIndexPosition = getTimeIndex(timetoMin, minInterval)

        let finalValue
        if (dateAvailable) {
          const valueArray = paramData?.values?.[dateKey]?.[timeIndexPosition]
          const valueArrayIsArray = isArray(valueArray)
          const arrayValues = valueArrayIsArray ? valueArray : []
          let value = arrayValues[propertyIndex]

          const isOutOfRange = value?.toString()?.startsWith("_")
          if (isOutOfRange && value?.toString()?.length > 1) {
            value = value?.toString()?.substring(1)
          }

          const isNull = constantValues?.includes(value)
          const isTimeline = axis?.axisName === "timeline"

          const checkedValue =
            isNull || (isTimeline ? false : !isFinite(toNumber(value)))
              ? null
              : isTimeline
              ? value
              : parseFloat(value)

          if ((isString(value) && value?.length === 0) || !valueArrayIsArray) {
            finalValue = prevValue
          } else {
            finalValue = checkedValue
          }

          if (timeIndexPosition === lastIndexPosition) {
            prevValue = null
          } else {
            prevValue = finalValue
          }
        } else {
          finalValue = null
          prevValue = null
        }

        const dateVal = currentTimestamp.format(reduxStoreTimeStampFormat)

        if (axis.axisName === "timeline") {
          if (!newDataTimeline?.[legendId]) {
            newDataTimeline[legendId] = {
              data: [],
              timestamps: []
            }
          }
        } else {
          if (!newData?.[legendId]) {
            newData[legendId] = {
              data: [],
              timestamps: []
            }
          }
        }

        if (axis?.axisName === "timeline") {
          newDataTimeline[legendId].data.push(finalValue)
          newDataTimeline[legendId].timestamps.push(dateVal)
        } else {
          newData[legendId].data.push(finalValue)
          newData[legendId].timestamps.push(dateVal)
        }
        if (!allTimestampsObject[dateVal]) {
          allTimestampsObject[dateVal] = 1
        }
        currentTimestamp = currentTimestamp.add(calculatedMin, "minutes")
      }
    }
  }
  const allTimestamps = Object.keys(allTimestampsObject).sort()
  const noDataArray = Object.keys(legends)?.map((key) => {
    const noData = (newData?.[key]?.data || [])?.every(
      (e) => !Boolean(e) && e !== 0
    )
    return noData
  })

  const isNoData = noDataArray?.every((e) => Boolean(e))

  assignAttributeStateToLegends(
    legends,
    oldLegends,
    assignedAttributes,
    legendAttributesCombination
  )

  // Create/update Exception Legends
  const exceptionData = inputData?.exceptions?.data || {}
  const exceptionLegends = generateExceptionLegends({
    exceptionData,
    chartInfo,
    equipmentList,
    selectedEquipID: equipmentId,
    oldTimeLineLegends
  })

  // Create/update Suppression Legends
  const suppressionData = inputData?.exceptions?.suppressed?.data || {}
  const suppressionLegends = generateSuppressionLegends({
    suppressionData,
    chartInfo,
    oldTimeLineLegends
  })

  // Assign Colors to new time line legends of all kinds
  assignAttributeStateToTLLegends(
    timeLineLegends,
    exceptionLegends,
    suppressionLegends,
    assignedAttributes,
    legendAttributesCombination,
    oldTimeLineLegends
  )

  const { timeLineBars, totalLanes } = transformTimeLinechartNew(
    timeLineLegends,
    newDataTimeline
  )

  const { exceptionsBars, exceptionLanes } = generateExceptionBars(
    exceptionData,
    exceptionLegends,
    chartInfo,
    totalLanes,
    startDate,
    endTimestamp,
    localTimeStamp
  )

  const suppressed =
    (inputData?.exceptions?.suppressed?.data &&
      inputData?.exceptions?.suppressed?.data[equipmentId]) ||
    {}
  const { suppressionBars } = generateSuppressionBars(
    suppressed,
    suppressionLegends,
    exceptionLanes,
    startDate,
    endTimestamp.format("YYYY-MM-DD")
  )

  const bars = [...timeLineBars, ...exceptionsBars, ...suppressionBars]

  const noDataTimelineArray = Object.keys(timeLineLegends)?.map((key) => {
    const noData = (newDataTimeline?.[key]?.data || [])?.every(
      (e) => !Boolean(e) && e !== 0
    )
    return noData
  })

  const isNoDataTimeline = noDataTimelineArray?.every((e) => Boolean(e))

  const isNoTimeLineData = timeLineBars?.every((obj) => !Boolean(obj?.value))

  timeLineLegends = {
    ...timeLineLegends,
    ...exceptionLegends,
    ...suppressionLegends
  }

  const axisList = getMergedYAxisList(chartInfo, addedAxisList)

  return {
    chartData: {
      data: isNoData ? {} : newData,
      timelineData: isNoDataTimeline ? {} : newDataTimeline
    },
    chartProps: {
      legends: Object.values(legends),
      axisList: axisList,
      timestamps: allTimestamps,
      timeLineLegends: timeLineLegends,
      timeLineBars:
        isNoData && isNoDataTimeline && isNoTimeLineData ? [] : bars,
      noDataAvailable: isNoData && isNoTimeLineData ? true : false,
      isdataprocessed: true,
      calculatedInterval: minimumCalculatedMin
    }
  }
}
